import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';

export const CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY = 'convenience-dispatch-deliveries';
// 本日なし判定専用: fixed route 選択時に対象日全件取得するクエリキー
export const CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY = 'convenience-dispatch-all-deliveries';

type DeliveriesResponse = {
  ids: number[];
  total_pages_count: number;
  data: PlanningsDeliveryWithLegRunEntity[];
};

const fetchDeliveries =
  (startOn: string, endOn: string, perPage: number, fixedRouteId: number | null) =>
  async ({ pageParam = 1 }: { pageParam?: number }) => {
    let url = `/api/v5/plannings/${startOn}/deliveries?end_on=${endOn}&page=${pageParam}&per_page=${perPage}`;
    if (fixedRouteId != null) {
      url += `&fixed_route_id=${fixedRouteId}`;
    }
    const response: AxiosResponse<DeliveriesResponse> = await axios.get(url);
    return response.data;
  };

// 本日なし判定専用: 対象日の deliveries を全件取得する (左カラム表示用の paginated query とは分離)
// fixed route 未選択時は enabled=false で取得しない
// page 1 で total_pages_count を確認し、残りページを並列取得して全件合算する。
// per_page は delivery × shipmentOrderLegRuns の入れ子でレスポンスが大きくなりやすいため
// 30 件ぶんに抑える (ブラウザの 6 並列制限内で 100 件を 1 RTT で取り切れる狙い)。
const ALL_DELIVERIES_PER_PAGE = 30;
const fetchAllDeliveries = async (startOn: string, endOn: string): Promise<PlanningsDeliveryWithLegRunEntity[]> => {
  const buildUrl = (page: number) =>
    `/api/v5/plannings/${startOn}/deliveries?end_on=${endOn}&page=${page}&per_page=${ALL_DELIVERIES_PER_PAGE}`;

  const firstRes: AxiosResponse<DeliveriesResponse> = await axios.get(buildUrl(1));
  const { total_pages_count, data: firstData } = firstRes.data;

  if (total_pages_count <= 1) return firstData;

  const remainingPages = Array.from({ length: total_pages_count - 1 }, (_, i) => i + 2);
  const remainingResponses = await Promise.all(
    remainingPages.map((page) => axios.get<DeliveriesResponse>(buildUrl(page)))
  );

  return [...firstData, ...remainingResponses.flatMap((res) => res.data.data)];
};

export const useConvenienceDispatchAllDeliveriesQuery = (
  startOn: string,
  endOn: string,
  enabled: boolean
) =>
  useQuery(
    [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY, startOn, endOn],
    () => fetchAllDeliveries(startOn, endOn),
    {
      enabled: enabled && Boolean(startOn) && Boolean(endOn),
      staleTime: Infinity,
    }
  );

export const useConvenienceDispatchDeliveriesQuery = (
  startOn: string,
  endOn: string,
  fixedRouteId: number | null,
  perPage = 10
) =>
  useInfiniteQuery(
    [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY, startOn, endOn, perPage, fixedRouteId],
    fetchDeliveries(startOn, endOn, perPage, fixedRouteId),
    {
      enabled: Boolean(startOn) && Boolean(endOn),
      staleTime: Infinity,
      getNextPageParam: (lastPage, allPages) =>
        allPages.length < lastPage.total_pages_count ? allPages.length + 1 : undefined,
    }
  );
